import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AsyncDialog, ConfirmationDialog, TypeConfirmationDialog } from '../../components/AsyncDialog';
import { AsyncDialogRef } from '../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialogData } from '../../components/AsyncDialog/ConfirmationDialog';
import { TypeConfirmationDialogData } from '../../components/AsyncDialog/TypeConfirmationDialog';
import { FakeButtonLink } from '../../components/FakeLink';
import { Field, FieldGrid } from '../../components/Section';
import { parseApiError } from '../../helpers/api';
import {
  useCancelAccountDeletionMutation,
  useCompleteAccountDeletionMutation,
  useFetchAccountDeletionBlockersQuery,
  useStartAccountDeletionMutation,
} from '../../redux/features/accountDeletion/accountDeletion.apiSlice';
import { locations } from '../../routes/locations';
import { AccountDeletionRequest, AccountDeletionStatus } from '../../types/accountDeletion';
import { formatDateTime } from '../../utils/date';
import {
  availableActions,
  blockerList,
  refusalMessage,
  reviewCountdown,
  startCheckReason,
  statusInfo,
} from './accountDeletion';

interface AccountDeletionDialogProps {
  request: AccountDeletionRequest | null;
  now: Date;
  canCancel: boolean;
  canProcess: boolean;
  onClose: () => void;
}

/** Empty when the step has not happened: `Field` shows "—" for it. */
const byEmployee = (at: string | null, by: number | null) => {
  if (!at) {
    return null;
  }

  return by ? `${formatDateTime(at)} · employee #${by}` : formatDateTime(at);
};

/** The four live checks madkhol-user runs again when Ops starts the deletion. */
function BlockerChecks({ requestId }: { requestId: number }) {
  const { data, isFetching, isError, error, refetch } = useFetchAccountDeletionBlockersQuery(requestId, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">Checks before deletion</Typography>
        <Tooltip title="Check again">
          <span>
            <IconButton size="small" onClick={() => refetch()} disabled={isFetching} aria-label="Check again">
              {isFetching ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {isError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {refusalMessage(parseApiError(error)?.data?.message)}
        </Alert>
      )}
      {data && (
        <>
          <List dense disablePadding>
            {blockerList(data.blockers).map(({ key, label, clear, blocked }) => (
              <ListItem key={key} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {blocked ? <ErrorOutlineIcon color="error" /> : <CheckCircleOutlineIcon color="success" />}
                </ListItemIcon>
                <ListItemText primary={label} secondary={blocked ? clear : 'Clear'} />
              </ListItem>
            ))}
          </List>
          <Typography variant="body2" color={data.canDelete ? 'success.main' : 'error.main'}>
            {data.canDelete
              ? 'Nothing left to clear. Confirm the ledger wallet balance is zero before running the runbook.'
              : 'The deletion cannot start until every check is clear.'}
          </Typography>
        </>
      )}
    </Box>
  );
}

/** One deletion request: its details, the live checks, and the actions its status and the employee's role allow. */
export function AccountDeletionDialog({ request, now, canCancel, canProcess, onClose }: AccountDeletionDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const confirmRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const typeConfirmRef = useRef<AsyncDialogRef<TypeConfirmationDialogData, boolean>>(null);
  const [cancelRequest, { isLoading: cancelling }] = useCancelAccountDeletionMutation();
  const [startDeletion, { isLoading: starting }] = useStartAccountDeletionMutation();
  const [completeDeletion, { isLoading: completing }] = useCompleteAccountDeletionMutation();
  const [refusal, setRefusal] = useState<string | null>(null);

  // The same query as the checks panel (RTK shares it): Start waits for it, since madkhol-user refuses a start with
  // anything left to clear.
  const checks = useFetchAccountDeletionBlockersQuery(request?.id ?? 0, {
    skip: request?.status !== AccountDeletionStatus.SUBMITTED,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => setRefusal(null), [request?.id]);

  if (!request) {
    return null;
  }

  const actions = availableActions(request, now, { canCancel, canProcess });
  const startReason =
    actions.startBlockedReason ||
    (actions.start
      ? startCheckReason({ isFetching: checks.isFetching, isError: checks.isError, canDelete: checks.data?.canDelete })
      : null);
  const status = statusInfo(request, now);
  const busy = cancelling || starting || completing;

  const run = async (action: (id: number) => { unwrap: () => Promise<unknown> }, done: string) => {
    setRefusal(null);

    try {
      await action(request.id).unwrap();
      enqueueSnackbar(done, { variant: 'success' });
      onClose();
    } catch (error) {
      setRefusal(refusalMessage(parseApiError(error)?.data?.message));
    }
  };

  const onCancel = async () => {
    const confirmed = await confirmRef.current?.show({
      title: 'Cancel this deletion request?',
      description: `The freeze on user #${request.userId} lifts at once and the account works normally again. Do this only when the user asked to keep the account.`,
      acceptButtonText: 'Cancel the request',
      rejectButtonText: 'Keep it',
    });

    if (confirmed) {
      await run(cancelRequest, 'Request cancelled. The account is no longer frozen.');
    }
  };

  const onStart = async () => {
    const confirmed = await typeConfirmRef.current?.show({
      title: 'Start the deletion',
      description: (
        <>
          From now on the request <strong>cannot be cancelled</strong>. The checks run again before it starts. Type the
          user ID <strong>{request.userId}</strong> to confirm.
        </>
      ),
      value: String(request.userId),
    });

    if (confirmed) {
      await run(startDeletion, 'Deletion started. Run the delete-user runbook, then mark it completed.');
    }
  };

  const onComplete = async () => {
    const confirmed = await typeConfirmRef.current?.show({
      title: 'Mark the deletion completed',
      description: (
        <>
          Only after the delete-user runbook has run for user <strong>{request.userId}</strong>. The user gets the
          completion SMS and the saved contact details are cleared. Type the user ID to confirm.
        </>
      ),
      value: String(request.userId),
    });

    if (confirmed) {
      await run(completeDeletion, 'Deletion marked completed. The completion SMS goes to the saved number.');
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <span>Deletion request #{request.id}</span>
          <Chip size="small" color={status.color} label={status.label} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <FieldGrid columns={2}>
          <Field label="User">
            <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.user(request.userId)}>
              {request.userId}
            </FakeButtonLink>
          </Field>
          <Field label="Reason" value={<code>{request.reasonCode}</code>} />
          <Field label="App language" value={request.language === 'ar' ? 'Arabic' : 'English'} />
          <Field label="Contact">
            {request.contactMobile || request.contactEmail ? (
              <>
                <div>{request.contactMobile}</div>
                <Typography variant="body2" color="text.secondary">
                  {request.contactEmail}
                </Typography>
              </>
            ) : (
              'Cleared after completion'
            )}
          </Field>
          <Field label="Submitted" value={formatDateTime(request.createdAt)} />
          <Field label="Review ends">
            {formatDateTime(request.reviewEndsAt)}
            {request.status === AccountDeletionStatus.SUBMITTED && (
              <Typography variant="body2" color="text.secondary">
                {reviewCountdown(request.reviewEndsAt, now)}
              </Typography>
            )}
          </Field>
          <Field label="Started" value={byEmployee(request.startedAt, request.startedBy)} />
          {request.status === AccountDeletionStatus.CANCELLED ? (
            <Field label="Cancelled" value={byEmployee(request.cancelledAt, request.cancelledBy)} />
          ) : (
            <Field label="Completed" value={byEmployee(request.completedAt, request.completedBy)} />
          )}
        </FieldGrid>

        {request.status === AccountDeletionStatus.SUBMITTED && (
          <>
            <Divider sx={{ my: 2 }} />
            <BlockerChecks requestId={request.id} />
          </>
        )}

        {request.status === AccountDeletionStatus.PROCESSING && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Next: run the delete-user runbook for user #{request.userId} and confirm the ledger wallet balance is zero.
            Then mark the request completed.
          </Alert>
        )}

        {refusal && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {refusal}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {actions.cancel && (
          <LoadingButton color="inherit" onClick={onCancel} loading={cancelling} disabled={busy}>
            Cancel request
          </LoadingButton>
        )}
        {(actions.start || actions.startBlockedReason) && (
          <Tooltip title={startReason || ''}>
            <span>
              <LoadingButton
                variant="contained"
                color="warning"
                onClick={onStart}
                loading={starting}
                disabled={busy || !actions.start || Boolean(startReason)}
              >
                Start deletion
              </LoadingButton>
            </span>
          </Tooltip>
        )}
        {actions.complete && (
          <LoadingButton variant="contained" color="error" onClick={onComplete} loading={completing} disabled={busy}>
            Mark completed
          </LoadingButton>
        )}
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
      <AsyncDialog ref={confirmRef} dialog={ConfirmationDialog} />
      <AsyncDialog ref={typeConfirmRef} dialog={TypeConfirmationDialog} />
    </Dialog>
  );
}
